// src/pages/ProductionPlanning.js
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, LinearProgress, Tabs, Tab, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Tooltip, Switch, FormControlLabel
} from '@mui/material';
import {
  TrendingUp, Add, Edit, Delete, AutoGraph,
  CalendarMonth, Analytics, Psychology,
  Notifications, Warning as WarningIcon,
  CheckCircle, BarChart, ShowChart, Download
} from '@mui/icons-material';
import { getData, saveData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';

const ProductionPlanning = ({ user }) => {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [historicalData, setHistoricalData] = useState([]);
  const [operatorReports, setOperatorReports] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('annual');
  const [viewMode, setViewMode] = useState('cards');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const plansData = await getData('production_plans') || [];
    const historical = await getData('operator_reports') || [];
    const opReports = await getData('operator_reports') || [];
    
    setPlans(plansData);
    setHistoricalData(historical);
    setOperatorReports(opReports);
  };

  // 📈 РОЗРАХУНОК ПРОГРЕСУ ВИКОНАННЯ
  const calculatePlanProgress = (plan) => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    if (plan.year !== currentYear) {
      return { overall: 0, monthly: Array(12).fill(0), totalActual: 0, totalPlanned: 0 };
    }

    const yearReports = operatorReports.filter(report => {
      const reportDate = new Date(report.date);
      return reportDate.getFullYear() === currentYear;
    });

    const monthlyProgress = Array(12).fill(0);
    
    for (let month = 0; month <= currentMonth; month++) {
      const monthReports = yearReports.filter(report => 
        new Date(report.date).getMonth() === month
      );
      
      const actualProduction = monthReports.reduce((sum, report) => 
        sum + (report.tunnelProduction || 0) + calculateWashProduction(report), 0
      );
      
      const plannedProduction = plan.monthlyBreakdown?.[month] || 0;
      monthlyProgress[month] = plannedProduction > 0 ? (actualProduction / plannedProduction) * 100 : 0;
    }

    const totalActual = yearReports.reduce((sum, report) => 
      sum + (report.tunnelProduction || 0) + calculateWashProduction(report), 0
    );
    
    const totalPlanned = plan.monthlyBreakdown?.slice(0, currentMonth + 1).reduce((a, b) => a + b, 0) || 1;
    const overallProgress = (totalActual / totalPlanned) * 100;

    return {
      overall: Math.min(overallProgress, 100),
      monthly: monthlyProgress,
      totalActual,
      totalPlanned
    };
  };

  const calculateWashProduction = (report) => {
    return (report.washCycles20kg || 0) * 20 + 
           (report.washCycles40kg || 0) * 40 + 
           (report.washCycles60kg || 0) * 55;
  };

  // 🤖 ПРОГНОЗУВАННЯ ВИКОНАННЯ ПЛАНУ
  const getCompletionForecast = (plan, progress) => {
    const currentMonth = new Date().getMonth();
    const remainingMonths = 11 - currentMonth;
    
    if (remainingMonths <= 0) return progress.overall;

    const monthlyAverage = progress.totalActual / (currentMonth + 1);
    const remainingProduction = plan.tunnelTarget + plan.washTarget - progress.totalActual;
    const forecastedCompletion = (progress.totalActual + (monthlyAverage * remainingMonths)) / (plan.tunnelTarget + plan.washTarget) * 100;

    return Math.min(forecastedCompletion, 100);
  };

  // 🔔 AI АНАЛІЗ ТА СПОВІЩЕННЯ
  const getAIAlerts = (plan, progress) => {
    const alerts = [];
    const forecast = getCompletionForecast(plan, progress);

    if (forecast < 85) {
      alerts.push({
        type: 'warning',
        message: `production_alerts_risk_completion`,
        percentage: Math.round(forecast),
        recommendation: 'production_recommendations_adjust_plan'
      });
    }

    if (progress.overall < 70 && new Date().getMonth() >= 6) {
      alerts.push({
        type: 'error', 
        message: 'production_alerts_insufficient_progress',
        recommendation: 'production_recommendations_urgent_review'
      });
    }

    const currentMonth = new Date().getMonth();
    if (progress.monthly[currentMonth] < 80) {
      alerts.push({
        type: 'warning',
        message: `production_alerts_low_performance`,
        month: getMonthName(currentMonth),
        percentage: Math.round(progress.monthly[currentMonth]),
        recommendation: 'production_recommendations_analyze_causes'
      });
    }

    return alerts;
  };

  const getMonthName = (monthIndex) => {
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return months[monthIndex];
  };

  // 🤖 AI РЕКОМЕНДАЦІЇ
  const getAIRecommendations = () => {
    const lastYearData = historicalData.filter(report => {
      const reportDate = new Date(report.date);
      const lastYear = new Date().getFullYear() - 1;
      return reportDate.getFullYear() === lastYear;
    });

    const monthlyAverages = calculateMonthlyAverages(lastYearData);
    const growthTrend = calculateGrowthTrend(historicalData);
    
    return {
      monthlyTargets: monthlyAverages.map(avg => Math.round(avg * (1 + growthTrend))),
      recommendedGrowth: Math.round(growthTrend * 100),
      seasonality: analyzeSeasonality(historicalData)
    };
  };

  const calculateMonthlyAverages = (data) => {
    const monthlySums = Array(12).fill(0);
    const monthlyCounts = Array(12).fill(0);

    data.forEach(report => {
      const month = new Date(report.date).getMonth();
      const production = (report.tunnelProduction || 0) + calculateWashProduction(report);
      monthlySums[month] += production;
      monthlyCounts[month]++;
    });

    return monthlySums.map((sum, index) => 
      monthlyCounts[index] > 0 ? sum / monthlyCounts[index] : 0
    );
  };

  const calculateGrowthTrend = (data) => {
    if (data.length < 2) return 0.1;
    
    const recentData = data.slice(-6);
    const olderData = data.slice(-12, -6);
    
    const recentAvg = recentData.reduce((sum, r) => sum + (r.tunnelProduction || 0) + calculateWashProduction(r), 0) / recentData.length;
    const olderAvg = olderData.reduce((sum, r) => sum + (r.tunnelProduction || 0) + calculateWashProduction(r), 0) / olderData.length;
    
    return olderAvg > 0 ? (recentAvg - olderAvg) / olderAvg : 0.1;
  };

  const analyzeSeasonality = (data) => {
    const monthlyTotals = Array(12).fill(0);
    
    data.forEach(report => {
      const month = new Date(report.date).getMonth();
      monthlyTotals[month] += (report.tunnelProduction || 0) + calculateWashProduction(report);
    });

    const max = Math.max(...monthlyTotals);
    return monthlyTotals.map(total => total / max);
  };

  // 🎯 КОМПОНЕНТ КАРТКИ ПЛАНУ
  const PlanCard = ({ plan }) => {
    const progress = calculatePlanProgress(plan);
    const forecast = getCompletionForecast(plan, progress);
    const alerts = getAIAlerts(plan, progress);

    return (
      <Card sx={{ height: '100%', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 } }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('production_plan')} {plan.year}
              </Typography>
              <Chip 
                label={t(`production_types_${plan.type}`)} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" display="block" color="text.secondary">
                {new Date(plan.createdAt).toLocaleDateString()}
              </Typography>
              <Chip 
                label={t(`production_status_${plan.status}`)} 
                size="small"
                color={plan.status === 'active' ? 'success' : 'default'}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">{t('production_progress_overall')}</Typography>
              <Typography variant="body2" fontWeight="bold">
                {Math.round(progress.overall)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress.overall}
              color={progress.overall >= 80 ? 'success' : progress.overall >= 60 ? 'warning' : 'error'}
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption">
                {t('production_actual')}: {Math.round(progress.totalActual).toLocaleString()} {t('production_units_kg')}
              </Typography>
              <Typography variant="caption">
                {t('production_planned')}: {Math.round(progress.totalPlanned).toLocaleString()} {t('production_units_kg')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" display="block" color="text.secondary">
              {t('production_forecast_title')}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color={forecast >= 85 ? 'success.main' : 'warning.main'}>
              {Math.round(forecast)}%
            </Typography>
          </Box>

          {alerts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {alerts.slice(0, 1).map((alert, index) => (
                <Alert 
                  key={index}
                  severity={alert.type}
                  sx={{ mb: 1 }}
                  icon={<WarningIcon />}
                >
                  <Typography variant="caption">
                    {t(alert.message, { percentage: alert.percentage, month: alert.month })}
                  </Typography>
                </Alert>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {t('production_created_by')}: {plan.createdBy}
            </Typography>
            
            <Box>
              <Tooltip title={t('production_actions_view_details')}>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPlan(plan);
                  }}
                >
                  <Analytics />
                </IconButton>
              </Tooltip>
              
              {(user.role === 'admin' || user.role === 'supervisor') && (
                <Tooltip title={t('production_actions_edit')}>
                  <IconButton 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPlan(plan);
                      setOpenEditDialog(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // 📊 КОМПОНЕНТ ТАБЛИЦІ ПЛАНІВ
  const PlansTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('production_table_year')}</TableCell>
            <TableCell>{t('production_table_type')}</TableCell>
            <TableCell>{t('production_table_tunnel_target')}</TableCell>
            <TableCell>{t('production_table_wash_target')}</TableCell>
            <TableCell>{t('production_table_progress')}</TableCell>
            <TableCell>{t('production_table_forecast')}</TableCell>
            <TableCell>{t('production_table_status')}</TableCell>
            <TableCell>{t('production_table_actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {plans
            .filter(plan => plan.type === activeTab)
            .map((plan) => {
              const progress = calculatePlanProgress(plan);
              const forecast = getCompletionForecast(plan, progress);
              
              return (
                <TableRow key={plan.id} hover>
                  <TableCell>
                    <Typography fontWeight="500">{plan.year}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={t(`production_types_${plan.type}`)} 
                      size="small" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>
                    {plan.tunnelTarget.toLocaleString()} {t('production_units_kg')}
                  </TableCell>
                  <TableCell>
                    {plan.washTarget.toLocaleString()} {t('production_units_kg')}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={progress.overall}
                        color={progress.overall >= 80 ? 'success' : progress.overall >= 60 ? 'warning' : 'error'}
                        sx={{ flex: 1, height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 40 }}>
                        {Math.round(progress.overall)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${Math.round(forecast)}%`}
                      size="small"
                      color={forecast >= 85 ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={t(`production_status_${plan.status}`)}
                      size="small"
                      color={plan.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={t('production_actions_view_details')}>
                        <IconButton 
                          size="small"
                          onClick={() => setSelectedPlan(plan)}
                        >
                          <Analytics />
                        </IconButton>
                      </Tooltip>
                      
                      {(user.role === 'admin' || user.role === 'supervisor') && (
                        <Tooltip title={t('production_actions_edit')}>
                          <IconButton 
                            size="small"
                            onClick={() => {
                              setSelectedPlan(plan);
                              setOpenEditDialog(true);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // 🆕 КОМПОНЕНТ СТВОРЕННЯ ПЛАНУ
  const CreatePlanDialog = () => {
    const [formData, setFormData] = useState({
      year: new Date().getFullYear(),
      type: 'annual',
      tunnelTarget: 0,
      washTarget: 0,
      productivityTarget: 50
    });

    const aiRecommendations = getAIRecommendations();

    const handleCreatePlan = async () => {
      const newPlan = {
        id: Date.now(),
        ...formData,
        createdAt: new Date().toISOString(),
        createdBy: user.username,
        status: 'active',
        monthlyBreakdown: aiRecommendations.monthlyTargets
      };

      const updatedPlans = [...plans, newPlan];
      await saveData('production_plans', updatedPlans);
      setPlans(updatedPlans);
      setOpenCreateDialog(false);
    };

    return (
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Add />
            {t('production_create_new_plan')}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Card sx={{ bgcolor: 'info.light', mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Psychology color="primary" />
                    <Typography variant="h6">{t('production_ai_title')}</Typography>
                  </Box>
                  <Typography variant="body2">
                    {t('production_ai_description')} - {aiRecommendations.recommendedGrowth}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Año"
                type="number"
                fullWidth
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('production_table_type')}</InputLabel>
                <Select
                  value={formData.type}
                  label={t('production_table_type')}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                >
                  <MenuItem value="annual">{t('production_types_annual')}</MenuItem>
                  <MenuItem value="quarterly">{t('production_types_quarterly')}</MenuItem>
                  <MenuItem value="monthly">{t('production_types_monthly')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label={t('production_table_tunnel_target')}
                type="number"
                fullWidth
                value={formData.tunnelTarget}
                onChange={(e) => setFormData({...formData, tunnelTarget: parseInt(e.target.value)})}
                helperText={`AI: ${Math.round(aiRecommendations.monthlyTargets.reduce((a, b) => a + b, 0) * 12)} ${t('production_units_kg')}`}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label={t('production_table_wash_target')}
                type="number"
                fullWidth
                value={formData.washTarget}
                onChange={(e) => setFormData({...formData, washTarget: parseInt(e.target.value)})}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Productividad"
                type="number"
                fullWidth
                value={formData.productivityTarget}
                onChange={(e) => setFormData({...formData, productivityTarget: parseInt(e.target.value)})}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Desglose Mensual (AI)</Typography>
              <Grid container spacing={1}>
                {aiRecommendations.monthlyTargets.map((target, index) => (
                  <Grid item xs={3} md={2} key={index}>
                    <Card variant="outlined">
                      <CardContent sx={{ p: 1, textAlign: 'center' }}>
                        <Typography variant="caption" display="block">
                          {getMonthName(index)}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {Math.round(target)} {t('production_units_kg')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreatePlan}>
            {t('production_create_new_plan')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // 👁️ КОМПОНЕНТ ПЕРЕГЛЯДУ ПЛАНУ
  const PlanViewDialog = () => {
    if (!selectedPlan) return null;

    const progress = calculatePlanProgress(selectedPlan);

    return (
      <Dialog open={!!selectedPlan} onClose={() => setSelectedPlan(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Analytics />
            {t('production_plan')} {selectedPlan.year} - {t(`production_types_${selectedPlan.type}`)}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Metas Principales</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{t('production_table_tunnel_target')}:</Typography>
                    <Typography fontWeight="bold">{selectedPlan.tunnelTarget.toLocaleString()} {t('production_units_kg')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{t('production_table_wash_target')}:</Typography>
                    <Typography fontWeight="bold">{selectedPlan.washTarget.toLocaleString()} {t('production_units_kg')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Productividad:</Typography>
                    <Typography fontWeight="bold">{selectedPlan.productivityTarget} kg/persona/hora</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{t('production_progress_overall')}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{t('production_actual')}:</Typography>
                    <Typography fontWeight="bold">{Math.round(progress.totalActual).toLocaleString()} {t('production_units_kg')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{t('production_planned')}:</Typography>
                    <Typography fontWeight="bold">{Math.round(progress.totalPlanned).toLocaleString()} {t('production_units_kg')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Progreso:</Typography>
                    <Typography fontWeight="bold">{Math.round(progress.overall)}%</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Desglose Mensual</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Mes</TableCell>
                      <TableCell align="right">Meta ({t('production_units_kg')})</TableCell>
                      <TableCell align="right">Real ({t('production_units_kg')})</TableCell>
                      <TableCell align="right">Progreso</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPlan.monthlyBreakdown?.map((target, index) => (
                      <TableRow key={index}>
                        <TableCell>{getMonthName(index)}</TableCell>
                        <TableCell align="right">{Math.round(target).toLocaleString()}</TableCell>
                        <TableCell align="right">-</TableCell>
                        <TableCell align="right">
                          <Chip label="Pendiente" size="small" color="default" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setSelectedPlan(null)}>Cerrar</Button>
          {(user.role === 'admin' || user.role === 'supervisor') && (
            <Button 
              variant="outlined" 
              color="error"
              onClick={async () => {
                const updatedPlans = plans.filter(p => p.id !== selectedPlan.id);
                await saveData('production_plans', updatedPlans);
                setPlans(updatedPlans);
                setSelectedPlan(null);
              }}
            >
              {t('production_actions_delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('production_title')}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={viewMode === 'table'}
                onChange={(e) => setViewMode(e.target.checked ? 'table' : 'cards')}
              />
            }
            label={t('production_table_view')}
          />
          
          {(user.role === 'admin' || user.role === 'supervisor') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenCreateDialog(true)}
            >
              {t('production_create_new_plan')}
            </Button>
          )}
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Psychology />
          <Box>
            <Typography fontWeight="500">
              {t('production_ai_title')}
            </Typography>
            <Typography variant="body2">
              {t('production_ai_description')}
            </Typography>
          </Box>
        </Box>
      </Alert>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab value="annual" label={t('production_tabs_annual')} />
        <Tab value="quarterly" label={t('production_tabs_quarterly')} />
        <Tab value="monthly" label={t('production_tabs_monthly')} />
      </Tabs>

      {viewMode === 'cards' ? (
        <Grid container spacing={3}>
          {plans
            .filter(plan => plan.type === activeTab)
            .map((plan) => (
              <Grid item xs={12} md={6} lg={4} key={plan.id}>
                <PlanCard plan={plan} />
              </Grid>
            ))}
        </Grid>
      ) : (
        <PlansTable />
      )}

      {plans.filter(plan => plan.type === activeTab).length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CalendarMonth sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('production_no_plans')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {(user.role === 'admin' || user.role === 'supervisor') 
              ? t('production_create_first_plan')
              : t('production_no_permission')
            }
          </Typography>
        </Box>
      )}

      {/* ДОДАЄМО ДІАЛОГИ */}
      <CreatePlanDialog />
      <PlanViewDialog />
    </Box>
  );
};

export default ProductionPlanning;